import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { Kontakt, Kunde } from '@gsm/entities';
import { DynamicFormControlEvent, DynamicFormModel, DynamicFormOptionConfig, DynamicSelectModel } from '@ng-dynamic-forms/core';
import { DynamicMaterialFormComponent } from '@ng-dynamic-forms/ui-material';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { compareProps } from '../../helpers/compare-props.helper';
import { setModelDisabled } from '../../helpers/disable-model.helper';
import { getDisplayName } from '../../helpers/display-name.helper';
import { formElementsInvalid } from '../../helpers/form-elements-invalid.helper';
import { getDynamicFormModelMap } from '../../helpers/model-map.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';

import { SelectEditModel } from '../select-edit/select-edit.model';
import { BENACHRICHTIGUNG_KEINE, BENACHRICHTIGUNG_OPTIONEN, KONTAKT_TEMPLATE } from './kontakt-angabe.config';
import { KontaktAngabeModel, SelectedKontaktTyp } from './kontakt-angabe.model';

@Component({
  selector:    'gsm-kontakt-angabe',
  templateUrl: './kontakt-angabe.component.html',
  styleUrls:   ['./kontakt-angabe.component.scss'],
  standalone:  true,
  imports: [
    MatCheckbox,
    FormsModule,
    DynamicMaterialFormComponent
  ]
})
export class KontaktAngabeComponent extends CustomDynamicFormControlComponent<KontaktAngabeModel> implements OnInit {
  private formService = inject(CustomDynamicFormService);

  formModel: DynamicFormModel;
  formGroup: UntypedFormGroup;

  hasContactInfo = true;
  noContactConfirmed = false;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    this.formModel = this.formService.fromJSON(KONTAKT_TEMPLATE);
    this.formGroup = this.formService.createFormGroup(this.formModel);

    if (this.model.disabled) {
      setModelDisabled(this.formModel, true);
    }

    if (this.model.kunden) {
      // extract models from array
      const modelMap = getDynamicFormModelMap(this.formModel as DynamicSelectModel<any>[]);
      const {
        EINSTIEG_FF_versicherungsnehmer: versicherungsnehmer,
        benachrichtigungskanal,
        email,
        mobile,
        infomobile
      } = modelMap;

      versicherungsnehmer.options = this.model.kunden.map(kunde => ({value: kunde.kunde}));
      versicherungsnehmer.compareWithFn = compareProps.bind(null, ['kundeId']);
      (versicherungsnehmer as SelectEditModel<Kunde>).displayLabelFn = getLabelForKunde;

      if (this.model.value) {
        this.formGroup.get('EINSTIEG_FF_versicherungsnehmer')
          .setValue(this.model.value.kunde);

        // update form models based on selected kunde
        this._updateFormFromValue(modelMap);
      } else {
        versicherungsnehmer.value = null;
        benachrichtigungskanal.value = '';
        email.value = '';
        mobile.value = '';
      }
    }

    this._updateModelValueFromForm();
    this._emitChange();
  }

  protected onDisabledChange() {
    setModelDisabled(this.formModel, this.model.disabled);
    super.onDisabledChange();
  }

  onFormChange(event: DynamicFormControlEvent): void {
    const versicherungsnehmerChanged: boolean = !!event.model && event.model.id === 'EINSTIEG_FF_versicherungsnehmer';
    const benachrichtigungChanged: boolean = !!event.model && event.model.id === 'benachrichtigungskanal';

    if (versicherungsnehmerChanged) {
      this.formGroup.get('benachrichtigungskanal')
        .setValue('');
    }

    if (versicherungsnehmerChanged || benachrichtigungChanged) {
      this.formGroup.get('email')
        .setValue('');
      this.formGroup.get('mobile')
        .setValue('');
    }

    this._updateModelValueFromForm();

    if (versicherungsnehmerChanged) {
      this._updateFormFromValue();
    }

    this._emitChange();
  }

  onNoContactCheckboxChange() {
    this._updateModelValueFromForm();
    this._emitChange();
  }

  private _emitChange() {
    this.group.controls[this.model.id].setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _updateModelValueFromForm() {
    const benachrichtigungskanal = this.formGroup.get('benachrichtigungskanal').value;
    const selectedKontaktTyp = Object.values(SelectedKontaktTyp)
      .includes(benachrichtigungskanal) ? benachrichtigungskanal : '';
    const selectedKontaktWert =
      selectedKontaktTyp === SelectedKontaktTyp.Mobile
        ? this.formGroup.get('mobile').value
        : selectedKontaktTyp === SelectedKontaktTyp.Email
          ? this.formGroup.get('email').value
          : '';
    const keinKontakt = this.noContactConfirmed || benachrichtigungskanal === BENACHRICHTIGUNG_KEINE;
    // const hint = this.formGroup.get('info').value

    this.model.value = {
      selectedKontaktTyp,
      selectedKontaktWert,
      keinKontakt,
      kunde: this.formGroup.get('EINSTIEG_FF_versicherungsnehmer').value
    };
  }

  private _updateFormFromValue(
    {
      info,
      benachrichtigungskanal,
      email,
      mobile,
      infomobile
    } = getDynamicFormModelMap(this.formModel as DynamicSelectModel<any>[])
  ) {
    if (this.model.value) {
      const {
        kunde: {
                 emailList,
                 mobileList
               },
        selectedKontaktTyp,
        keinKontakt
      } = this.model.value;
      const hasEmail = !!(emailList && emailList.length);
      const hasMobile = !!(mobileList && mobileList.length);
      this.hasContactInfo = hasEmail || hasMobile;
      this.noContactConfirmed = this.hasContactInfo === false && keinKontakt === true;

      benachrichtigungskanal.hidden = !this.hasContactInfo;
      info.hidden = !this.hasContactInfo;
      benachrichtigungskanal.options = getBenachrichtigungOptions(hasEmail, hasMobile);
      const benachrichtigungstyp = this.hasContactInfo && keinKontakt ? BENACHRICHTIGUNG_KEINE : selectedKontaktTyp;
      this.formGroup.get('benachrichtigungskanal')
        .setValue(benachrichtigungstyp);

      // this is necessary despite relations because otherwise the validation doesn't work properly
      if (benachrichtigungstyp === BENACHRICHTIGUNG_KEINE) {
        email.hidden = true;
        mobile.hidden = true;
        infomobile.hidden = true;
      }

      if (hasEmail) {
        email.options = mapKontakteToKontaktOptions(emailList);
        if (this.model.value.selectedKontaktTyp === SelectedKontaktTyp.Email) {
          this.formGroup.get('email')
            .setValue(this.model.value.selectedKontaktWert);
        }
      }

      if (hasMobile) {
        mobile.options = mapKontakteToKontaktOptions(mobileList);
        if (this.model.value.selectedKontaktTyp === SelectedKontaktTyp.Mobile) {
          this.formGroup.get('mobile')
            .setValue(this.model.value.selectedKontaktWert);
        }
      }
    }
  }

  private _getErrors(): ValidationErrors {
    const invalid =
      formElementsInvalid(this.formGroup, this.formModel) || (this.hasContactInfo === false && this.noContactConfirmed === false);

    return invalid ? {kontaktAngabe: true} : null;
  }
}

function getLabelForKunde(kunde: Kunde): string {
  const {
    vorname,
    nachname,
    namenZusatz,
    emailList,
    mobileList
  } = kunde;
  const hauptEmail = getHauptKontakt(emailList);
  const hauptMobile = getHauptKontakt(mobileList);

  return getDisplayName([
    vorname,
    nachname
  ], [
    namenZusatz,
    hauptEmail,
    hauptMobile
  ]);
}

function getHauptKontakt(kontakte: Kontakt[]): string {
  if (kontakte && kontakte.length) {
    const haupt = kontakte.find(kontakt => kontakt.haupt);
    return haupt ? haupt.wert : kontakte[0].wert;
  }
  return '';
}

function mapKontakteToKontaktOptions(kontakte: Kontakt[] = []): DynamicFormOptionConfig<string>[] {
  return kontakte.map(kontakt => ({
    label: kontakt.wert + (kontakt.haupt ? ' (Haupt)' : ''),
    value: kontakt.wert
  }));
}

function getBenachrichtigungOptions(hasEmail: boolean, hasMobile: boolean): DynamicFormOptionConfig<string>[] {
  return BENACHRICHTIGUNG_OPTIONEN.filter(option => {
    switch (option.value) {
      case SelectedKontaktTyp.Email:
        return hasEmail;
      case SelectedKontaktTyp.Mobile:
        return hasMobile;
      default:
        return true;
    }
  });
}
